import SaveIcon from "@mui/icons-material/FileDownloadOutlined";
import PrintIcon from "@mui/icons-material/PrintOutlined";
import { Box, Card, CardMedia, IconButton, Stack } from "@mui/material";
import type { SyntheticEvent } from "react";
import { useCallback, useState } from "react";
import type { FileDownloadInfo } from "@entrilia/shared/api/fileTypes";
import type { ApiResponse } from "@entrilia/shared/api/types";
import useResizeObserver from "@entrilia/shared/hooks/useResizeObserver";
import { logError } from "@entrilia/shared/logging";
import { downloadFileFromUrl } from "@entrilia/shared/services/downloadFile";
import { printBlob } from "@entrilia/shared/utilities/printHelper";
import CitationHighlightOverlay from "./CitationHighlightOverlay";
import type { CitationHighlight, Size } from "./citationHighlightsHelper";
import { getContainedImageRect } from "./citationHighlightsHelper";

interface Props {
  fileInfo: FileDownloadInfo;
  // Original fileInfo is not enough: the downloadUrl might expire before user attempts to download / print the file
  getFileDownloadInfoCallback: () => Promise<ApiResponse<FileDownloadInfo>>;
  highlights?: CitationHighlight[];
  selectedCitationId?: string;
}

const ImagePreview = ({ fileInfo, getFileDownloadInfoCallback, highlights, selectedCitationId }: Props) => {
  const [saving, setSaving] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [imageEl, setImageEl] = useState<HTMLImageElement | null>(null);
  const [elementSize, setElementSize] = useState<Size>();
  const [naturalSize, setNaturalSize] = useState<Size>();

  const handleResize = useCallback<ResizeObserverCallback>((entries) => {
    const rect = entries[0]?.contentRect;
    if (rect) {
      setElementSize({ width: rect.width, height: rect.height });
    }
  }, []);

  useResizeObserver(imageEl, undefined, handleResize);

  const handleImageLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    setNaturalSize({ width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight });
  };

  const handlePrintClick = async () => {
    setPrinting(true);

    try {
      const downloadInfoResp = await getFileDownloadInfoCallback();

      if (downloadInfoResp.data === undefined) {
        logError(downloadInfoResp.error, "[ImagePreview] handlePrintClick");
        return;
      }

      const blobResp = await fetch(downloadInfoResp.data.downloadUrl);
      if (!blobResp.ok) {
        logError(blobResp.statusText, "[ImagePreview] download image blob");
        return;
      }

      const blob = await blobResp.blob();
      await printBlob(blob);
    } catch (error) {
      logError(error, "[ImagePreview] handlePrintClick");
    } finally {
      setPrinting(false);
    }
  };

  const handleDownloadClick = async () => {
    setSaving(true);

    const resp = await getFileDownloadInfoCallback();
    if (resp.data !== undefined) {
      await downloadFileFromUrl(resp.data.downloadUrl);
    } else {
      logError(resp.error, "[ImagePreview] handleDownloadClick");
    }

    setSaving(false);
  };

  const imageContentRect =
    highlights?.length && elementSize && naturalSize ? getContainedImageRect(elementSize, naturalSize) : undefined;

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "flex-end", py: 1, px: 2.5 }}>
        <Stack direction="row" sx={{ alignItems: "center" }}>
          <IconButton onClick={handlePrintClick} loading={printing}>
            <PrintIcon />
          </IconButton>
          <IconButton onClick={handleDownloadClick} loading={saving}>
            <SaveIcon />
          </IconButton>
        </Stack>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, pb: 2 }}>
        <Card sx={{ flexGrow: 1, position: "relative" }} variant="outlined">
          <CardMedia
            ref={setImageEl}
            component="img"
            src={fileInfo.downloadUrl}
            onLoad={handleImageLoad}
            onContextMenu={(e) => e.preventDefault()}
            sx={{ objectFit: "contain", border: 0, height: "100%" }}
          />
          {imageContentRect && (
            <Box sx={{ position: "absolute", pointerEvents: "none", ...imageContentRect }}>
              {highlights?.map((highlight, index) => (
                <CitationHighlightOverlay
                  // eslint-disable-next-line @eslint-react/no-array-index-key
                  key={`${highlight.citationId}-${index}`}
                  highlight={highlight}
                  isSelected={highlight.citationId === selectedCitationId}
                />
              ))}
            </Box>
          )}
        </Card>
      </Box>
    </>
  );
};

export default ImagePreview;
